import React from "react";
import awardsData from "../constants/awardsData";

const selectionStages = [
  {
    title: "Call for Nominations",
    text: "Nominations are invited from individual professionals, research teams, higher education institutions, innovation centers, start-ups, corporate units, and incubators.",
    bullets: [
      "Entries may be submitted through self-nomination, peer or professional recommendation, institutional leadership, or Advisory Council recommendations.",
      "All nominations must be supported by verifiable documentation and evidence of contribution.",
    ],
  },
  {
    title: "Preliminary Review & Eligibility Check",
    text: "Each nomination undergoes an initial screening to verify eligibility, completeness, and relevance to the selected award category.",
    bullets: [
      "Submissions lacking required documentation, failing eligibility norms, or falling outside the award scope will not proceed further.",
    ],
  },
  {
    title: "Expert Scrutiny & Evaluation",
    text: "Eligible nominations are evaluated by an eminent jury of academicians, senior researchers, industry leaders, policy advisors, and global domain experts.",
  },
  {
    title: "Consolidation & Final Recommendation",
    text: "Based on cumulative evaluations, a shortlist of high-ranking nominations is prepared. Scoring trends, expert observations, and supporting evidence are carefully reviewed during final consolidation.",
  },
  {
    title: "Final Approval & Ratification",
    text: "Final selections are ratified by the Awards Steering Committee comprising senior academicians, industry veterans, and independent observers.",
    bullets: [
      "Only nominations demonstrating exceptional merit, credibility, and sustained impact are approved.",
      "The committee reserves the right to withhold or reclassify awards if standards are unmet.",
    ],
  },
];

const criteria = [
  ["Original Contribution & Thought Leadership", "Novel ideas, intellectual depth, advancement of knowledge"],
  ["Measurable Impact & Value Creation", "Demonstrated outcomes and real-world relevance"],
  ["Technical Depth & Professional Excellence", "Rigor, quality, and domain expertise"],
  ["Implementation Effectiveness", "Practical execution, efficiency, reliability"],
  ["Leadership & Collaborative Approach", "Team building, partnerships, mentorship"],
  ["Sustainability & Long-Term Relevance", "Scalability, continuity, future readiness"],
  ["Ethical Conduct & Social Responsibility", "Responsible innovation and societal sensitivity"],
];

function AwardCard({ award }) {
  return (
    <article className="rounded-2xl bg-white p-6 shadow-md transition hover:shadow-lg">
      <h3 className="mb-2 text-lg font-semibold text-gray-800">🏆 {award.title}</h3>
      {award.desc && <p className="whitespace-pre-line text-gray-600">{award.desc}</p>}
    </article>
  );
}

export default function AwardsPage() {
  return (
    <main className="space-y-12 px-4 py-10 md:px-10 lg:px-20">
      <header className="space-y-4 text-center">
        <h1 className="m-3 text-4xl font-semibold">ICCTRDA 2026 Awards &amp; Recognitions</h1>
        <hr className="m-auto mb-4 w-96 border-2 border-black" />
        <p className="mx-auto max-w-3xl text-lg text-gray-700 md:text-xl">
          Celebrating Research Excellence, Innovation and Industry Impact
        </p>
        <p className="mx-auto max-w-4xl text-gray-600">
          The Organizing Committee of ICCTRDA 2026 proudly announces a series of
          prestigious awards and recognitions to honor researchers, academicians,
          professionals, institutions, and innovators who have demonstrated
          outstanding contributions across academia, industry, and society.
        </p>
        <p className="text-sm text-gray-500">
          The awards will be conferred during the Valedictory Ceremony at Duy Tan
          University, Da Nang, Vietnam.
        </p>
      </header>

      <section aria-labelledby="award-categories">
        <h2 id="award-categories" className="mb-6 text-2xl font-semibold">🏆 Award Categories</h2>
        <div className="max-h-[70vh] space-y-10 overflow-y-auto rounded-2xl border border-gray-200 bg-gray-50 p-4 pr-3 shadow-inner md:p-6">
          {awardsData.map((category) => (
            <div key={category.title}>
              <h3 className="mb-4 text-xl font-semibold text-gray-800">{category.title}</h3>
              <div className="grid gap-6 md:grid-cols-2">
                {category.data.map((award) => <AwardCard key={award.title} award={award} />)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section aria-labelledby="selection-process">
        <h2 id="selection-process" className="mb-3 text-2xl font-semibold">Selection Process Overview</h2>
        <p className="mb-6 text-lg text-gray-700">A transparent, rigorous, and multi-stage evaluation framework</p>
        <div className="space-y-5">
          {selectionStages.map((stage, index) => (
            <article key={stage.title} className="rounded-2xl bg-gray-50 p-6 shadow-sm">
              <h3 className="mb-2 text-lg font-semibold text-gray-800">{index + 1}. {stage.title}</h3>
              <p className="text-gray-700">{stage.text}</p>
              {stage.bullets && (
                <ul className="mt-3 list-disc space-y-2 pl-6 text-gray-700">
                  {stage.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                </ul>
              )}
            </article>
          ))}
        </div>
      </section>

      <section aria-labelledby="transparency-integrity">
        <h2 id="transparency-integrity" className="mb-4 text-2xl font-semibold">Transparency &amp; Integrity Assurance</h2>
        <p className="text-gray-700">
          The selection process follows strict principles of fairness, confidentiality,
          and conflict-of-interest management. Jury decisions are final and binding
          to preserve the prestige and integrity of the awards.
        </p>
      </section>

      <section aria-labelledby="selection-criteria">
        <h2 id="selection-criteria" className="mb-4 text-2xl font-semibold">Selection Criteria Framework</h2>
        <p className="mb-5 text-gray-700">
          Each criterion is evaluated independently to ensure a balanced assessment
          of academic merit, industry relevance, and societal value.
        </p>
        <div className="overflow-x-auto rounded-2xl shadow-md">
          <table className="min-w-full border-collapse bg-white text-left">
            <thead className="bg-blue-900 text-white">
              <tr>
                <th className="border border-blue-800 px-4 py-3">Criterion</th>
                <th className="border border-blue-800 px-4 py-3">Focus Area</th>
              </tr>
            </thead>
            <tbody>
              {criteria.map(([criterion, focus]) => (
                <tr key={criterion} className="odd:bg-gray-50">
                  <th scope="row" className="border px-4 py-3 font-semibold text-gray-800">{criterion}</th>
                  <td className="border px-4 py-3 text-gray-700">{focus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="recognition">
        <h2 id="recognition" className="mb-4 text-2xl font-semibold">🎓 Recognition and Benefits</h2>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>An Official Certificate of Excellence</li>
          <li>A Commemorative Plaque</li>
          <li>Public recognition during the Valedictory Ceremony</li>
          <li>Feature on the ICCTRDA 2026 website, social media, and conference proceedings</li>
        </ul>
      </section>

      <section className="rounded-2xl bg-gray-100 p-6 shadow-md" aria-labelledby="important-dates">
        <h2 id="important-dates" className="mb-2 text-xl font-semibold">🗓️ Important Dates</h2>
        <p><strong>Last date for nomination:</strong> 15th November, 2026</p>
        <p><strong>Announcement of Result:</strong> 20th November, 2026</p>
        <a
          href="https://forms.gle/iSy3NexUuX3mDy2A6"
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-block rounded-lg bg-blue-900 px-5 py-2 font-semibold text-white hover:bg-blue-700"
        >
          📝 Nomination Form
        </a>
      </section>

      <p className="mx-auto max-w-3xl text-center text-gray-600">
        ICCTRDA 2026 reaffirms its dedication to advancing innovation, fostering
        collaboration, and bridging the gap between research and industry through these awards.
      </p>
    </main>
  );
}

